/* Utils */
import { ConnectionType } from '../../constants';
import { closeApp as commonCloseApp } from '../../functions';
import importScript from '../../importScript';
import { relaunchSubscription, sendRequestToParent } from './messages';

const KEYS_TO_REGISTER = [
  'MediaPlayPause',
  'MediaPause',
  'MediaPlay',
  'MediaStop',
  'MediaRewind',
  'MediaTrackPrevious',
  'MediaFastForward',
  'MediaTrackNext',
  'ChannelUp',
  'ChannelDown',
];
const WEBAPIS_SCRIPT = '/$WEBAPIS/webapis/webapis.js';

let webapisAPIPromise;

export async function tizenAPI({ functionExpression, args }) {
  if (window.tizen) {
    const expressions = functionExpression.split('.');
    let previous = window.tizen;
    for (const expression of expressions) {
      if (expression.includes('()')) {
        const functionName = expression.replace('()', '');
        const functionArguments = args?.[functionName] || [];
        previous = previous[functionName](...functionArguments);
      } else {
        previous = previous[expression];
      }
    }
    return previous;
  }

  return sendRequestToParent('tizenAPI', {
    functionExpression,
    args,
  });
}

export async function getWebapisApi() {
  if (!webapisAPIPromise) {
    webapisAPIPromise = importScript(WEBAPIS_SCRIPT);
  }
  await webapisAPIPromise;
  return window.webapis;
}

export async function closeApp() {
  try {
    await tizenAPI({
      functionExpression: 'application.getCurrentApplication().exit()',
    });
  } catch (error) {
    console.error('utils>platform>tizen>general closeApp', error);
    commonCloseApp();
  }
}

export async function sendAppToBackground() {
  try {
    await tizenAPI({
      functionExpression: 'application.getCurrentApplication().hide()',
    });
  } catch (error) {
    console.error('utils>platform>tizen>general sendAppToBackground', error);
    closeApp();
  }
}

async function getCapability(capability) {
  return tizenAPI({
    functionExpression: 'systeminfo.getCapability()',
    args: { getCapability: [capability] },
  });
}

async function getDeviceInfoFunction() {
  const deviceInfoFromTizenApi = {};
  const webapis = await getWebapisApi();

  try {
    const versionPromise = getCapability('http://tizen.org/feature/platform.version');
    const screenWidthPromise = getCapability('http://tizen.org/feature/screen.width');
    const screenHeightPromise = getCapability('http://tizen.org/feature/screen.height');

    deviceInfoFromTizenApi.sdkVersion = await versionPromise;
    deviceInfoFromTizenApi.screenWidth = await screenWidthPromise;
    deviceInfoFromTizenApi.screenHeight = await screenHeightPromise;
  } catch (error) {
    console.error('TizenUtils>getDeviceInfo error', error);
  }

  return {
    version: webapis?.productinfo.getFirmware(),
    modelName: webapis?.productinfo.getRealModel(),
    modelNameAscii: webapis?.productinfo.getModel(),
    uhd: webapis?.productinfo.isUdPanelSupported(),
    uhd8K: webapis?.productinfo.is8KPanelSupported?.(),
    hdr: webapis?.avinfo.isHdrTvSupport(),
    ...deviceInfoFromTizenApi,
  };
}

let deviceInfoPromise;
export function getDeviceInfo() {
  deviceInfoPromise = deviceInfoPromise || getDeviceInfoFunction();
  return deviceInfoPromise;
}

export async function registerKeys() {
  try {
    await tizenAPI({
      functionExpression: 'tvinputdevice.registerKeyBatch()',
      args: { registerKeyBatch: [KEYS_TO_REGISTER] },
    });
  } catch (error) {
    console.error('utils>platform>tizen>general registerKeys', error);
  }
}

export function isTizen() {
  return window.tizen || navigator.userAgent.includes('Tizen');
}

let timezonePromise;
export function getTimezone() {
  timezonePromise = timezonePromise || tizenAPI({
    functionExpression: 'time.getLocalTimezone()',
    args: {},
  });
  return timezonePromise;
}

let appInfoPromise;
export function getAppInfo() {
  appInfoPromise = appInfoPromise || tizenAPI({
    functionExpression: 'application.getCurrentApplication().appInfo',
    args: {},
  });
  return appInfoPromise;
}

let appIdPromise;
export function getAppId() {
  appIdPromise = appIdPromise || getAppInfo().then(({ id }) => id);
  return appIdPromise;
}

let versionIdPromise;
export function getAppVersion() {
  versionIdPromise = versionIdPromise || getAppInfo().then(({ version }) => version);
  return versionIdPromise;
}

async function getTizenNetworkConnectionData({ onSuccess, onFailure }) {
  try {
    const webapis = await getWebapisApi();
    const MAP_CONNECTION_TYPE = {
      [webapis.network.NetworkActiveConnectionType.WIFI]: ConnectionType.WIFI,
      [webapis.network.NetworkActiveConnectionType.CELLULAR]: ConnectionType.CELLULAR,
      [webapis.network.NetworkActiveConnectionType.ETHERNET]: ConnectionType.ETHERNET,
    };
    const tizenConnectionType = webapis.network.getActiveConnectionType();

    if (tizenConnectionType === webapis.network.NetworkActiveConnectionType.DISCONNECTED) {
      onSuccess({
        isConnected: false,
        connectionType: undefined,
      });
      return;
    }
    onSuccess({
      isConnected: webapis.network.isConnectedToGateway(),
      connectionType: MAP_CONNECTION_TYPE[tizenConnectionType],
    });
  } catch (error) {
    onFailure(error);
  }
}

export async function getConnectionStatus({
  subscribe,
  onSuccess,
  onFailure,
}) {
  getTizenNetworkConnectionData({ onSuccess, onFailure });
  if (subscribe) {
    const webapis = await getWebapisApi();
    webapis.network.addNetworkStateChangeListener(() => {
      getTizenNetworkConnectionData({ onSuccess, onFailure });
    });
  }
}

function getKeymanagerAliases() {
  return tizenAPI({
    functionExpression: 'keymanager.getDataAliasList()',
  });
}

export async function saveCredentials(dataKey, dataValue, onSuccess = undefined) {
  try {
    await tizenAPI({
      functionExpression: 'keymanager.saveData()',
      // eslint-disable-next-line unicorn/no-null -- The API requires to be null
      args: { saveData: [dataKey, dataValue, null, onSuccess || null] },
    });
  } catch (error) {
    console.error('utils>platform>tizen>general saveCredentials', error);
  }
}

export async function getCredentials(dataKey) {
  try {
    const aliasList = await getKeymanagerAliases();

    if (aliasList.some(({ name }) => name === dataKey)) {
      return tizenAPI({
        functionExpression: 'keymanager.getData()',
        args: { getData: [{ name: dataKey }] },
      });
    }
  } catch (error) {
    console.error('utils>platform>tizen>general getCredentials', error);
  }
  return undefined;
}

export async function removeCredentials(dataKey) {
  try {
    const aliasList = await getKeymanagerAliases();

    if (aliasList.some(({ name }) => name === dataKey)) {
      tizenAPI({
        functionExpression: 'keymanager.removeData()',
        args: { removeData: [{ name: dataKey }] },
      });
    }
  } catch (error) {
    console.error('utils>platform>tizen>general removeCredentials', error);
  }
}

const SMART_HUB_PREVIEW_PAYLOAD_KEY = 'PAYLOAD';
export async function getUrlToOpen() {
  const { appControl } = await tizenAPI({
    functionExpression: 'application.getCurrentApplication().getRequestedAppControl()',
  });

  if (!appControl) return undefined;

  const { uri, data } = appControl;

  const { value: [payload] = [] } = data?.find(
    ({ key }) => key === SMART_HUB_PREVIEW_PAYLOAD_KEY,
  ) || {};

  if (payload) {
    return JSON.parse(payload).values;
  }

  return uri;
}

export function addRelaunchEventListener(callback) {
  const handleEvent = async () => {
    const url = await getUrlToOpen();
    callback(url);
  };
  relaunchSubscription.addSubscriber(handleEvent);
  return () => {
    relaunchSubscription.removeSubscriber(handleEvent);
  };
}
